/**
 * 시도 이름을 짧게 쓰기 위한 대응표.
 *
 * 카카오가 주는 주소는 대부분 시도를 이미 줄여서 준다(서울특별시 → '서울',
 * 경기도 → '경기'). 그런데 특별자치시·도는 통째로 온다. 저장된 장소 6,730건을
 * 세어보면 제주특별자치도 183 · 전북특별자치도 137 · 강원특별자치도 129 ·
 * 세종특별자치시 18 · 전남광주통합특별시 372 — 전체의 12.5%다.
 *
 * 카카오의 축약표가 옛 이름(강원도·전라북도…)을 열쇠로 삼기 때문으로 보인다.
 * 이름이 바뀐 뒤로는 열쇠가 안 맞아 원문이 그대로 통과한다. 제주는 2006년부터
 * 특별자치도인데 지금도 안 줄어드는 걸 보면 최근에 바뀌어서가 아니라 애초에
 * 표에 없는 것이다.
 *
 * 뒤쪽 항목들은 방어용이다. 지금 데이터에는 옛 전체 이름이 하나도 없지만,
 * 다른 경로로 들어온 주소가 그럴 수 있다.
 */
const SIDO_SHORT: Record<string, string> = {
  제주특별자치도: '제주',
  전북특별자치도: '전북',
  강원특별자치도: '강원',
  세종특별자치시: '세종',
  전남광주통합특별시: '전남광주',

  서울특별시: '서울',
  부산광역시: '부산',
  대구광역시: '대구',
  인천광역시: '인천',
  광주광역시: '광주',
  대전광역시: '대전',
  울산광역시: '울산',
  경기도: '경기',
  강원도: '강원',
  충청북도: '충북',
  충청남도: '충남',
  전라북도: '전북',
  전라남도: '전남',
  경상북도: '경북',
  경상남도: '경남',
}

/** 시·군·구·읍·면으로 끝나면 행정구역 이름으로 본다 */
const ADMIN_UNIT = /(시|군|구|읍|면)$/

/**
 * 주소에서 시도 이름만 짧게 바꾸고 나머지는 그대로 둔다.
 *
 *   '전남광주통합특별시 동구 동명로14번길 26' → '전남광주 동구 동명로14번길 26'
 *   '제주특별자치도 제주시 애월읍 하귀로 59'  → '제주 제주시 애월읍 하귀로 59'
 *   '서울 강남구 테헤란로 123'                → 그대로
 *
 * getShortAddress와 쓰임이 다르다. 저쪽은 '어느 동네인가'만 보여주는 자리(랭킹,
 * A/B 비교)라 도로명을 버리지만, 바텀시트·영수증처럼 주소를 다 보여주는 자리는
 * 뒤를 남겨야 한다. 예전에는 그런 자리에 원문이 그대로 나가서 같은 동네가
 * 화면마다 '전남광주'와 '전남광주통합특별시'로 갈려 보였다.
 *
 * 저장은 카카오가 주는 원문 그대로 둔다. 검색·매칭이 그 값을 쓰고, 관리자
 * 화면도 저장된 것을 그대로 보여줘야 하기 때문이다. 줄이는 것은 표시할 때만.
 */
export const shortenSido = (address: string): string => {
  const trimmed = address.trim()
  if (trimmed === '') {
    return address
  }

  const spaceAt = trimmed.indexOf(' ')
  const sido = spaceAt === -1 ? trimmed : trimmed.slice(0, spaceAt)
  const short = SIDO_SHORT[sido]

  if (!short) {
    return address
  }
  return spaceAt === -1 ? short : short + trimmed.slice(spaceAt)
}

/**
 * 주소에서 시도와 그 아래 행정구역까지만 뽑는다.
 *
 *   '서울 강남구 테헤란로 123'              → '서울 강남구'
 *   '제주특별자치도 제주시 애월읍 하귀로 59' → '제주 제주시'
 *   '세종특별자치시 나성로 125-16'          → '세종'
 *
 * 둘째 어절을 무조건 붙이지 않는 이유
 * --------------------------------
 * 세종에는 시·군·구 단계가 없어서 시도 바로 다음에 도로명이 온다. 그대로
 * 떼어 쓰면 '세종특별자치시 나성로'처럼 길 이름을 동네 이름 자리에 그리게 된다.
 *
 * 그래서 둘째 어절이 행정구역처럼 생겼을 때만 붙인다. 저장된 주소를 전수로
 * 확인하니 둘째 어절이 시/군/구/읍/면으로 안 끝나는 것은 전국에서 16건뿐이고
 * 전부 세종이었다 — 이 규칙 때문에 달라지는 곳은 그 16건이 전부다.
 */
export const getShortAddress = (address: string): string => {
  const parts = address.trim().split(/\s+/)
  const sido = SIDO_SHORT[parts[0]] ?? parts[0]
  const second = parts[1]

  if (second && ADMIN_UNIT.test(second)) {
    return `${sido} ${second}`
  }
  return sido || address
}
